/**
 * FP3-033 — Session History Printout.
 *
 * Date-range filter (30 / 60 / 90 / custom) on top, hidden in print.
 * Loads sessions via the existing `GET /api/sessions?student_id=…` —
 * the server already filters by visibility.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import {
  PrintShell,
  PrintAccessGate,
  classifyError,
} from "@/components/print/PrintShell";

function isoDateNDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export default function PrintStudentSessions() {
  const { studentId } = useParams();
  const [range, setRange] = useState("30"); // "30" | "60" | "90" | "custom"
  const [customFrom, setCustomFrom] = useState(isoDateNDaysAgo(30));
  const [customTo, setCustomTo] = useState(todayIso());
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const [student, setStudent] = useState(null);
  const [sessions, setSessions] = useState([]);

  const fromDate = useMemo(() => {
    if (range === "custom") return customFrom;
    return isoDateNDaysAgo(parseInt(range, 10));
  }, [range, customFrom]);
  const toDate = useMemo(() => {
    if (range === "custom") return customTo;
    return todayIso();
  }, [range, customTo]);

  useEffect(() => {
    let cancelled = false;
    api.getStudent(studentId)
      .then((r) => { if (!cancelled) setStudent(r.data); })
      .catch(() => { /* student header is decorative; sessions own the error */ });
    return () => { cancelled = true; };
  }, [studentId]);

  useEffect(() => {
    let cancelled = false;
    setPhase("loading");
    api.getSessions({ student_id: studentId })
      .then((r) => {
        if (cancelled) return;
        const rows = (r.data || [])
          .filter((s) => {
            const d = s.date || "";
            return d >= fromDate && d <= toDate;
          })
          // Iter 45s parity — never include future-dated sessions in print.
          .filter((s) => (s.date || "") <= todayIso());
        setSessions(rows);
        setPhase("ready");
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.detail || err.message);
        setPhase(classifyError(err));
      });
    return () => { cancelled = true; };
  }, [studentId, fromDate, toDate]);

  const name = student ? `${student.first_name} ${student.last_name}` : "Student";
  const totalMinutes = sessions.reduce(
    (acc, s) => acc + Number(s.minutes_actual ?? s.minutes_scheduled ?? 0),
    0
  );

  return (
    <PrintShell
      title="Session History"
      subtitle={`${name} · ${fromDate} – ${toDate}`}
      testId="print-student-sessions"
    >
      {/* Date-range selector — screen only, hidden on print. */}
      <div
        className="fp-print-section fp-no-print"
        data-fp-no-print="true"
        data-testid="sessions-range-bar"
        style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}
      >
        <div className="fp-print-section-label">Range</div>
        {["30", "60", "90"].map((r) => (
          <button
            key={r}
            type="button"
            data-testid={`sessions-range-${r}`}
            className="fp-print-action"
            onClick={() => setRange(r)}
            style={range === r
              ? { background: "var(--fp-print-accent)", color: "#fff", borderColor: "var(--fp-print-accent)" }
              : {}}
          >
            Last {r} days
          </button>
        ))}
        <button
          type="button"
          data-testid="sessions-range-custom"
          className="fp-print-action"
          onClick={() => setRange("custom")}
          style={range === "custom"
            ? { background: "var(--fp-print-accent)", color: "#fff", borderColor: "var(--fp-print-accent)" }
            : {}}
        >
          Custom
        </button>
        {range === "custom" && (
          <>
            <input
              type="date"
              data-testid="sessions-range-from"
              value={customFrom}
              max={customTo}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="fp-print-action"
              style={{ padding: "4px 8px" }}
            />
            <span>→</span>
            <input
              type="date"
              data-testid="sessions-range-to"
              value={customTo}
              min={customFrom}
              max={todayIso()}
              onChange={(e) => setCustomTo(e.target.value)}
              className="fp-print-action"
              style={{ padding: "4px 8px" }}
            />
          </>
        )}
      </div>

      <PrintAccessGate phase={phase} error={error}>
        <div className="fp-print-section">
          <div className="fp-print-grid-3">
            <div>
              <div className="fp-print-section-label">Sessions in range</div>
              <div
                data-testid="sessions-count"
                style={{ fontWeight: 700, fontSize: 14 }}
              >
                {sessions.length}
              </div>
            </div>
            <div>
              <div className="fp-print-section-label">Total minutes</div>
              <div data-testid="sessions-total-minutes" style={{ fontWeight: 700, fontSize: 14 }}>
                {totalMinutes}
              </div>
            </div>
            <div>
              <div className="fp-print-section-label">Period</div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{fromDate} – {toDate}</div>
            </div>
          </div>
        </div>

        <div className="fp-print-section" data-testid="sessions-table-section">
          {sessions.length === 0 ? (
            <div className="fp-print-empty" style={{ padding: "12px 16px" }}>
              No sessions in the selected range.
            </div>
          ) : (
            <table className="fp-print-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Type</th>
                  <th>Attendance</th>
                  <th>Minutes</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.id} data-testid={`session-row-${s.id}`}>
                    <td>{s.date || "—"}</td>
                    <td>{s.start_time || "—"}</td>
                    <td>{s.session_type || s.kind || "session"}</td>
                    <td>{s.attendance || "—"}</td>
                    <td>{s.minutes_actual ?? s.minutes_scheduled ?? "—"}</td>
                    <td style={{ fontSize: 11 }}>
                      {(s.notes || "").slice(0, 200) || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </PrintAccessGate>
    </PrintShell>
  );
}
